import React, { useMemo } from "react";
import { useRouter } from "next/router";
import {
  Event as EventType,
  Community,
  User,
} from "@dreambigger/shared/src/types";
import {
  Card,
  Group,
  ShareBar,
  DecoratedImage,
  Divider,
  Typography,
} from "@dreambigger/design-system";
import { EditButton, EventCtaCard, AttendeeCard, DescriptionCard, EventThankYouCard } from "../ui";
import { formatInTimeZone } from "date-fns-tz";
import { isAfter } from "date-fns";
import { getFormattedEventTimes, getDateFnsTimeZone } from "@dreambigger/shared/src/utils";
import dedent from "dedent";

const THANK_YOU_NOTES_BUTTON_TITLE_DEFAULT = "SEND THANK YOU NOTES";
const THANK_YOU_NOTES_TITLE_DEFAULT = "Show how much you appreciate your friends who made this project a success!";

const { Title, Text } = Typography;

type EventContentProps = {
  event: EventType;
  community: Community;
  coverImage: string;
  user?: User;
};

const handleClick = () => {
  window.location.pathname = window.location.pathname.replace("detail", "edit");
};

const EventContent = ({
  event,
  community,
  coverImage,
  user,
}: EventContentProps) => {
  // Guard against no event being passed in.
  if (!event) {
    return <>There was an error loading this page. Please try again later.</>;
  }

  const router = useRouter();

  // Store community keyword. Example: Thrivent prefers to refer to their events as "projects."
  const keyword = community.meta?.marketing?.keywordUpper || "Event ";
  // Also store a lowercase version of it and store whether it begins with a vowel.
  const lcKeyword =
    community.meta?.marketing?.keywordUpper?.toLowerCase() || "event ";
  const startsWithVowel = keyword.startsWith("A" || "E" || "I" || "O" || "U");
  // ------------------------------------------------------------------------------------------------------------------
  // 1 DEFINE "SHARE BY EMAIL" MESSAGE
  // ------------------------------------------------------------------------------------------------------------------

  // EMAIL SHARE BUTTON CONFIG FUNCTIONS - These functions can be expanded to generate custom email messages for future clients using "if" checks.

  // --- Configure Email Subject/Header ---
  const getShareSubject = () => {
    return `You're invited to participate in ${
      event.name || "a special event"
    } by ${community.organization.name}`;
  };

  // --- Get dates and times for email message ---
  const eventTimes =
    event.startAt && event.endAt && event.timeZone
      ? getFormattedEventTimes(event.startAt, event.endAt, event.timeZone)
      : undefined;

  // --- Configure Email Message
  const getShareMessage = () => {
    // SHARE MESSAGE:
    // * Hello! I have some exciting news to share with you!
    //   I'm hosting a project called "Tat Thank You Test 1" on  May 20, 2022 - May 29, 2022 (9:00 AM - 8:00 PM PDT). I'm very excited about this project and would love your help!
    //   There are lots of ways to get involved! Would you consider partnering with me in this project and sharing it with others?
    //   I appreciate your consideration. You can learn more and sign up at: (url)
    const shareMessage = dedent(`
    Hello! I have some exciting news to share with you!
      
    I'm ${event.isAdmin ? `hosting` : `participating in`} ${
      startsWithVowel ? `an ${lcKeyword}` : `a ${lcKeyword}`
    } ${event.name && `called "${event.name}"`} ${
      eventTimes?.dates &&
      `on  ${eventTimes.dates} ${
        eventTimes.times && `(${eventTimes.times} ${eventTimes?.abbrTimezone})`
      }`
    }. I'm very excited about this ${lcKeyword} and would love your help!
    
    There are lots of ways to get involved! Would you consider partnering with me in this ${lcKeyword} and sharing it with others?
      
    Thanks for your consideration! You can learn more and sign up at:
    `); // NOTE - the share message will have the current url appended to it.
    return shareMessage;
  };

  // ------------------------------------------------------------------------------------------------------------------
  //  2 SUBCOMPONENTS
  // ------------------------------------------------------------------------------------------------------------------
  // MAIN HEADER ----------------------------------------
  const MainContentHeaderComponent = useMemo(() => {
    return (
      <>
        <DecoratedImage
          src={coverImage}
          alt="Event Img"
          cornerButtonEnabled={event.isAdmin} // User should be able to edit this picture if user is admin of event.
          cornerButtonText="Edit"
          cornerButtonFunction={handleClick}
          cornerButtonTextExpanded="Edit Image"
          cornerButtonIcon="images"
          style={{
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            boxShadow: "rgb(0 0 0 / 16%) 0px 1px 4px",
          }}
          className="z-99"
        />
      </>
    );
  }, [event]);

  // SHARE BAR ------------------------------------------

  const ShareComponent = useMemo(() => {
    return (
      <div className="md_ph-5">
        <style>
          @import
          url('https://fonts.googleapis.com/css2?family=The+Girl+Next+Door&display=swap');
        </style>
        <h3
          className="pv-0 mb-1 md_mb-4 f-7 md_f-9"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "20px",
            fontFamily: "The Girl Next Door",
            color: "darkgray",
          }}
        >
          Help spread the word!
        </h3>
        <div className="w-100">
          {/* ShareBar at non-mobile sizes */}
          <div className="dn md_db">
            <ShareBar
              labeledButtons={true}
              liftOnHover={true}
              rectangularButtons={true}
              email={user !== undefined && true} // Disable if there is no logged-in user.
              clipboard={false}
              shareMessageBody={getShareMessage()}
              shareMessageSubject={getShareSubject()}
              rsvpUrl={
                // if the user owns the event, this replaces the email button with an "invite" button that launches the "rsvp flow"
                event.isAdmin
                  ? `${router.basePath}/actions/invites/event/${event.id}`
                  : undefined
              }
            />
          </div>
          {/* ShareBar at mobile sizes */}
          <div className="w-50 md_dn mh-auto">
            <ShareBar
              liftOnHover={true}
              rectangularButtons={true}
              email={user !== undefined && true} // Disable if there is no logged-in user.
              clipboard={false}
              shareMessageBody={getShareMessage()}
              shareMessageSubject={getShareSubject()}
              rsvpUrl={
                // if the user owns the event, this replaces the email button with an "invite" button that launches the "rsvp flow"
                event.isAdmin
                  ? `${router.basePath}/actions/invites/event/${event.id}`
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    );
  }, [event]);

  // DESCRIPTION -----------------------------------------
  const MainContentDescription = useMemo(() => {
    return (
      <Group spacing={3} className="ph-5 items-start">
        {/* About this Event/Project/Campaign etc */}
        {event.description && (
          <>
            <Title level={4} className={`${event.isAdmin && "mb-0"}`}>
              About This {keyword}
            </Title>
            {/* If the event belongs to the user, allow the description to be edited. */}

            {event.isAdmin && (
              <div
                className="primary fwb underline subtleGrow f-4 pointer"
                onClick={handleClick}
              >
                Edit
              </div>
            )}
            <Text style={{ whiteSpace: "pre-line" }}>{event.description}</Text>
          </>
        )}
        {/* In Support Of... */}
        {event.charity.name && (
          <>
            <Title level={4}>In Support Of</Title>
            <Text>{event.charity.name}</Text>
            {community.organization.meta.externalVettingClientImage && (
              <img
                src={community.organization.meta.externalVettingClientImage}
                alt="Organization Image"
              />
            )}
          </>
        )}
      </Group>
    );
  }, [event]);

  // check if event is over to show Thank You note button  ---------
  const eventEnded = useMemo(() => {
    const { timeZone, endAt } = event;
    if (!timeZone || !endAt) {
      return false;
    }

    const timezone = getDateFnsTimeZone(timeZone);
    const endDate = formatInTimeZone(endAt, timezone, "EEEE, MMM dd, yyyy")
    return isAfter(new Date(), new Date(endDate))
  }, [event]);

  // USER ACTIONS -----------------------------------------
  const UserActionsMenu = useMemo(() => {
    const descriptionProps = { event, community };
    const thankYouNoteProps = { 
      buttonPath: `/events/${event.id}/thankyou`,
      buttonTitle: THANK_YOU_NOTES_BUTTON_TITLE_DEFAULT,
      title: THANK_YOU_NOTES_TITLE_DEFAULT,
    }
    return (
      <>
        {event.isAdmin && (
          <div className="mb-5">
            <EditButton keyword={keyword} />
          </div>
        )}
        <div className="mb-5">
          <DescriptionCard {...descriptionProps} />
        </div>
        {community?.meta?.eventDetailCta && (
          <div className="mb-5">
            <EventCtaCard {...community.meta.eventDetailCta} />
          </div>
        )}
        {event.isAdmin && (
            <div className="mb-5">
              <AttendeeCard eventId={event.id} />
            </div>
        )}
        {event.isAdmin && eventEnded && (
          <div className="mb-5">
            <EventThankYouCard {...thankYouNoteProps} />
          </div>
        )}
      </>
    );
  }, [event]);

  // ------------------------------------------------------------------------------------------------------------------
  // 3 RENDER
  // ------------------------------------------------------------------------------------------------------------------
  return (
    <>
      <Group lg="horizontal" spacing={5} style={{ minHeight: "600px" }}>
        {/* LEFT COLUMN */}
        <Group className="lg_w-66">
          {/* Image - If one has been passed in */}
          {coverImage && MainContentHeaderComponent}
          <Card
            className="mt-2"
            style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
            bodyStyle={{ padding: "10px 0" }}
          >
            {ShareComponent}
            <Divider className="mv-6" />
            {MainContentDescription}
          </Card>
        </Group>
        {/* RIGHT COLUMN */}
        <Group className="lg_w-33">{UserActionsMenu}</Group>
      </Group>
    </>
  );
};

export default EventContent;
